import React, { useState } from "react";
import { useSelector } from "react-redux";
import DataTable from "react-data-table-component";

// ── API CONFIG ──────────────────────────────────────────────────────────────
const API_URL =
    "https://api.myad360.com/api/v1/FNV89uJ9sud/rest_api/get_invoice.php";
// ───────────────────────────────────────────────────────────────────────────

// "2026-01" → "January 2026"
const formatMonth = (monthStr) => {
    if (!monthStr) return "—";
    const [year, mon] = monthStr.split("-");
    const date = new Date(Number(year), Number(mon) - 1, 1);
    return date.toLocaleString("en-US", { month: "long", year: "numeric" });
};

// format currency
const fmtINR = (val) =>
    "$" +
    Number(val || 0).toLocaleString("en-us", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

// ── SCOPED STYLES ───────────────────────────────────────────────────────────
const INVOICE_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,500;0,9..144,600&family=Manrope:wght@400;500;600;700;800&display=swap');
  .inv-display { font-family: 'Fraunces', ui-serif, Georgia, serif; }
  .inv-body { font-family: 'Manrope', ui-sans-serif, system-ui, sans-serif; }
`;

// ── CUSTOM STYLES ───────────────────────────────────────────────────────────
const customStyles = {
    table: {
        style: {
            border: "1px solid #F0DCC8",
        },
    },
    headRow: {
        style: {
            backgroundColor: "#FFF4E5",
            fontWeight: "600",
            fontSize: "13px",
            minHeight: "44px",
            color: "#92400E",
        },
    },
    rows: {
        style: {
            minHeight: "48px",
            fontSize: "13px",
            color: "#1C1410",
            "&:not(:last-of-type)": {
                borderBottomColor: "#F5DFC5",
            },
        },
    },
    cells: {
        style: {
            paddingTop: "8px",
            paddingBottom: "8px",
        },
    },
    pagination: {
        style: {
            color: "#4D3E30",
            borderTopColor: "#F5DFC5",
        },
        pageButtonsStyle: {
            borderRadius: "8px",
            height: "32px",
            width: "32px",
            padding: "4px",
            margin: "0 3px",
            cursor: "pointer",
            transition: "0.2s",
            color: "#D97706",
            fill: "#D97706",
            backgroundColor: "transparent",
            "&:disabled": {
                cursor: "not-allowed",
                color: "#E7D4BC",
                fill: "#E7D4BC",
            },
            "&:hover:not(:disabled)": {
                backgroundColor: "#FFF4E5",
            },
            "&:focus": {
                outline: "none",
                backgroundColor: "#FDE8CD",
            },
        },
    },
};
// ───────────────────────────────────────────────────────────────────────────

const InvoiceTable = () => {
    const { userLoginToken } = useSelector((state) => state.user);

    const [month, setMonth] = useState(() => {
        const now = new Date();
        const year = now.getFullYear();
        const mon = String(now.getMonth() + 1).padStart(2, "0");
        return `${year}-${mon}`;
    });
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // ── FETCH ───────────────────────────────────────────────────────────────
    const fetchInvoices = async () => {
        setLoading(true);
        setError(null);
        setData([]);

        try {
            const response = await fetch(API_URL, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    token: userLoginToken,
                },
                body: JSON.stringify({ month }),
            });

            if (!response.ok) {
                throw new Error(`Server error: ${response.status} ${response.statusText}`);
            }

            const result = await response.json();

            if (result.status !== 1) {
                throw new Error(result.message || result.msg || "Failed to fetch invoices.");
            }

            const rows = (result.data || []).map((row, idx) => ({
                id: row.id ?? idx + 1,
                invoice_number: row.invoice_number ?? "—",
                month: row.month ?? "",
                payout: row.payout ?? "0",
                deductions: row.deductions ?? "0",
                net_payment: row.net_payment ?? "0",
                payment_date: row.payment_date ?? "—",
                reference_id: row.reference_id ?? "—",
                company_name: row.company_name ?? "—",
                gst_no: row.gst_no ?? "—",
                pdf_url: row.pdf_url ?? null,
            }));

            setData(rows);

            if (rows.length === 0) {
                setError("No invoice records found for the selected month.");
            }
        } catch (err) {
            setError(err.message || "Failed to fetch invoices. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // ── OPEN PDF IN NEW TAB ─────────────────────────────────────────────────
    const handleOpenPDF = (row) => {
        if (!row.pdf_url) {
            alert("PDF not available for this invoice.");
            return;
        }
        window.open(row.pdf_url, "_blank", "noopener,noreferrer");
    };

    // ── COLUMNS ─────────────────────────────────────────────────────────────
    const columns = [
        {
            name: "Item",
            selector: (row) => row.month,
            grow: 2,
            cell: (row, index) => (
                <span className="text-[#1C1410] font-medium">
                    {index + 1}. {formatMonth(row.month)}
                </span>
            ),
        },
        {
            name: "Quantity",
            center: true,
            cell: () => (
                <span className="text-[#8A7560]">1</span>
            ),
        },
        // {
        //     name: "Payout ($)",
        //     center: true,
        //     selector: (row) => row.payout,
        //     cell: (row) => (
        //         <span className="text-gray-600">{fmtINR(row.payout)}</span>
        //     ),
        // },
        // {
        //     name: "Deductions ($)",
        //     center: true,
        //     selector: (row) => row.deductions,
        //     cell: (row) => (
        //         <span className=""> {fmtINR(row.deductions)}</span>
        //     ),
        // },
        {
            name: "Net Payment ($)",
            center: true,
            selector: (row) => row.net_payment,
            cell: (row) => (
                <span className="font-semibold text-[#1C1410]">
                    {fmtINR(row.net_payment)}
                </span>
            ),
        },
        {
            name: "Action",
            center: true,
            cell: (row) => (
                <button
                    onClick={() => handleOpenPDF(row)}
                    title="Download Invoice PDF"
                    className="flex items-center gap-1 px-3 py-1.5 bg-[#FFF4E5] hover:bg-[#FDE8CD] text-[#D97706] text-xs font-semibold rounded-lg transition-colors"
                >
                    <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="7 10 12 15 17 10" />
                        <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    PDF
                </button>
            ),
        },
    ];

    // ── TOTALS ──────────────────────────────────────────────────────────────
    const totalPayout = data.reduce((s, r) => s + Number(r.payout || 0), 0);
    const totalDeductions = data.reduce((s, r) => s + Number(r.deductions || 0), 0);
    const totalNet = data.reduce((s, r) => s + Number(r.net_payment || 0), 0);

    return (
        <div className="inv-body p-0 bg-[#FFFBF5]">
            <style>{INVOICE_STYLES}</style>
            <div>
                <h2 className="inv-display text-2xl font-semibold text-[#1C1410] mb-4">
                    Invoice
                </h2>
            </div>

            <div
                className="bg-white p-4 sm:p-6 shadow-[0_10px_30px_-18px_rgba(22,34,27,0.25)]"
                style={{ borderTop: "4px solid #D97706" }}
            >
                {/* Header controls */}
<div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
    <div className="flex flex-col sm:flex-row sm:items-end gap-3 w-full sm:w-auto">
        <div className="w-full sm:w-auto">
            <label className="block text-xs text-[#8A7560] font-medium mb-1">
                Select Month
            </label>
            <input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="w-full sm:w-auto border border-[#F0DCC8] px-3 py-2 rounded-lg text-sm text-[#1C1410] focus:outline-none focus:border-[#D97706] focus:ring-2 focus:ring-[#FDE8CD] transition-colors"
            />
        </div>
        <button
            onClick={fetchInvoices}
            disabled={loading}
            className="w-full sm:w-auto bg-gradient-to-r from-[#D97706] to-[#EA580C] hover:from-[#EA580C] hover:to-[#D97706] text-white px-6 py-2.5 font-medium text-sm rounded-lg disabled:opacity-60 transition-all"
        >
            {loading ? "Loading..." : "Generate"}
        </button>
    </div>
</div>

                {/* Error banner */}
                {error && (
                    <div className="mb-4 px-4 py-3 bg-[#FEF3E5] border border-[#F5C77E] rounded-lg text-sm text-[#92400E]">
                        ⚠️ {error}
                    </div>
                )}

                {/* Table */}
                <div className="border border-[#F0DCC8] rounded-xl p-4">
                    <DataTable
                        columns={columns}
                        data={data}
                        customStyles={customStyles}
                        pagination
                        progressPending={loading}
                        progressComponent={
                            <div className="py-8 text-sm text-[#8A7560]">
                                Fetching invoices…
                            </div>
                        }
                        noDataComponent={
                            <div className="py-8 text-sm text-[#8A7560]">
                                Select a month and click Generate to load invoices.
                            </div>
                        }
                    />

                    {/* Totals footer */}
                    {/* {data.length > 0 && (
                        <div className="border-t border-gray-200 pt-3 mt-1 flex flex-col items-end gap-1 pr-2">
                            <div className="flex gap-6 text-sm text-gray-500">
                                <span className="w-32 text-right">Total Payout:</span>
                                <span className="w-28 text-right font-medium text-gray-700">
                                    {fmtINR(totalPayout)}
                                </span>
                            </div>
                            <div className="flex gap-6 text-sm text-gray-500">
                                <span className="w-32 text-right">Total Deductions:</span>
                                <span className="w-28 text-right font-medium text-red-500">
                                    - {fmtINR(totalDeductions)}
                                </span>
                            </div>
                            <div className="flex gap-6 text-sm font-bold text-gray-800 border-t border-gray-200 pt-2 mt-1">
                                <span className="w-32 text-right">Net Payment:</span>
                                <span className="w-28 text-right text-blue-600">
                                    {fmtINR(totalNet)}
                                </span>
                            </div>
                        </div>
                    )} */}
                </div>
            </div>
        </div>
    );
};

export default InvoiceTable;